import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, switchMap, catchError, tap } from 'rxjs/operators';
import * as LeadersActions from './leaders.actions';
import { LeaderService } from '../../core/services/leader.service';
import { CacheService } from '../../core/services/cache.service';
import { Leader } from '../../core/models/leader.model';

@Injectable()
export class LeadersEffects {
  private actions$ = inject(Actions);
  private leaderService = inject(LeaderService);
  private cacheService = inject(CacheService);

  loadLeaders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeadersActions.loadLeaders),
      switchMap(async () => {
        const cached = await this.cacheService.getItem<Leader[]>('leaders:all');
        if (cached) {
          return LeadersActions.loadLeadersSuccess({ leaders: cached });
        }

        return new Promise<ReturnType<typeof LeadersActions.loadLeadersSuccess | typeof LeadersActions.loadLeadersFailure>>(
          resolve => {
            this.leaderService.getAllLeaders().subscribe({
              next: async leaders => {
                await this.cacheService.setItem('leaders:all', leaders);
                resolve(LeadersActions.loadLeadersSuccess({ leaders }));
              },
              error: err => resolve(LeadersActions.loadLeadersFailure({
                error: err?.message ?? 'Failed to load leaders'
              }))
            });
          }
        );
      })
    )
  );

  loadFeatured$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeadersActions.loadFeaturedLeaders),
      switchMap(() =>
        this.leaderService.getFeaturedLeaders().pipe(
          map(leaders => LeadersActions.loadFeaturedLeadersSuccess({ leaders })),
          catchError(() => of(LeadersActions.loadFeaturedLeadersSuccess({ leaders: [] })))
        )
      )
    )
  );

  invalidateCache$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LeadersActions.invalidateCache),
      tap(() => this.cacheService.invalidatePrefix('leaders:'))
    ),
    { dispatch: false }
  );
}
